import { useEffectWithController } from '@carvana/merch-react-hooks';
import { isSuccess } from '@carvana/merch-result';
import { useContext } from 'react';
import { HomeApiClientId } from '~/constants/common/ApiClientId';
import { useResolvedOnServer } from '~/context/common/DeferredResolutionContext';
import { useEventCorrelationId } from '~/context/common/StaticContext';
import FinanceContext from '~/context/home/FinanceContext';
import useSkipFirstFetchIfResolvedOnServer from '~/hooks/common/useSkipFirstFetchIfResolvedOnServer';
import fetchCreditStatus from '~/services/common/fetchCreditStatus';
import { generateErrorLogger } from '~/utilities/loggers';

export default function useGetCreditStatus(isLoggedIn: boolean) {
  const {
    financeInfo: { setData, setError, setLoading },
    fetchFinanceTrigger
  } = useContext(FinanceContext);
  const eventCorrelationId = useEventCorrelationId();
  const { creditStatus: creditStatusResolvedOnServer } = useResolvedOnServer();
  const shouldSkipFetch = useSkipFirstFetchIfResolvedOnServer(
    creditStatusResolvedOnServer,
    fetchFinanceTrigger
  );

  useEffectWithController(
    controller => {
      if (!isLoggedIn) {
        setData({});

        return;
      }

      if (shouldSkipFetch(fetchFinanceTrigger)) return;

      setLoading();

      fetchCreditStatus(eventCorrelationId, HomeApiClientId, controller)
        .then(({ data }) => {
          setData(isSuccess(data) ? data.content : {});
        })
        .catch(
          generateErrorLogger(
            'Get finance info',
            { correlationId: eventCorrelationId },
            { callback: setError, controller }
          )
        );
    },
    [
      eventCorrelationId,
      fetchFinanceTrigger,
      isLoggedIn,
      setData,
      setError,
      setLoading,
      shouldSkipFetch
    ]
  );
}
